import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  getMyOrganizations, canManage,
  type Organization, type OrgMembership, type OrgRole,
} from '../lib/organizations';

/**
 * Which account the person is acting as: their own, or one of their fleets.
 *
 * Ported from the mobile app's `contexts/OrganizationContext.tsx` and behaves
 * identically, so switching to a fleet on the phone and on the web mean the
 * same thing. Two rules carried over deliberately:
 *
 *  · **null activeOrg means personal, and personal is the default.** A person
 *    who happens to belong to a fleet is not put into it on sign-in.
 *  · The remembered choice is **only restored if they are still a member.**
 *    Otherwise someone removed from a fleet reopens the site still pointed at
 *    it, sees an empty garage, and reports it as data loss.
 */

interface OrganizationContextValue {
  loading: boolean;
  memberships: OrgMembership[];
  activeOrg: Organization | null;
  activeRole: OrgRole | null;
  isManager: boolean;
  isBusiness: boolean;
  setActiveOrg: (org: Organization | null) => void;
  refresh: () => Promise<void>;
}

const PERSONAL: OrganizationContextValue = {
  loading: false,
  memberships: [],
  activeOrg: null,
  activeRole: null,
  isManager: false,
  isBusiness: false,
  setActiveOrg: () => {},
  refresh: async () => {},
};

const OrganizationContext = createContext<OrganizationContextValue>(PERSONAL);

/** Safe outside the provider: returns plain personal mode rather than throwing. */
export const useOrganization = () => useContext(OrganizationContext);

const storageKey = (uid: string) => `active_org_${uid}`;

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState<OrgMembership[]>([]);
  const [activeOrg, setActiveOrgState] = useState<Organization | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id ?? null;
      setUserId(uid);

      if (!uid) {
        setMemberships([]);
        setActiveOrgState(null);
        return;
      }

      const mine = await getMyOrganizations();
      setMemberships(mine);

      // Restore the remembered fleet only if it is still one of theirs.
      let remembered: string | null = null;
      try {
        remembered = window.localStorage.getItem(storageKey(uid));
      } catch {
        // Private windows and blocked site data throw on access. Personal mode
        // is the correct fallback, so this is not worth surfacing.
      }
      const match = remembered ? mine.find((m) => m.organization.id === remembered) : undefined;
      setActiveOrgState(match ? match.organization : null);
    } catch {
      // A failure here must not lock someone out of their personal account.
      setMemberships([]);
      setActiveOrgState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      setLoading(true);
      load();
    });
    return () => sub?.subscription?.unsubscribe();
  }, [load]);

  const setActiveOrg = useCallback((org: Organization | null) => {
    setActiveOrgState(org);
    if (!userId) return;
    try {
      if (org) window.localStorage.setItem(storageKey(userId), org.id);
      else window.localStorage.removeItem(storageKey(userId));
    } catch {
      // Non-fatal: the choice simply will not survive a reload.
    }
  }, [userId]);

  const value = useMemo<OrganizationContextValue>(() => {
    const activeRole = activeOrg
      ? memberships.find((m) => m.organization.id === activeOrg.id)?.role ?? null
      : null;
    return {
      loading,
      memberships,
      activeOrg,
      activeRole,
      isManager: canManage(activeRole),
      isBusiness: memberships.length > 0,
      setActiveOrg,
      refresh: load,
    };
  }, [loading, memberships, activeOrg, setActiveOrg, load]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
